import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { AdminTabBar } from '@/components/AdminTabBar';
import { AdvisoryAuditLogTab } from '@/components/screens/advisory/AdvisoryAuditLogTab';
import type { AdvisoryAuditLogTabHandle } from '@/components/screens/advisory/AdvisoryAuditLogTab';
import { AdvisoryReviewStatusTab } from '@/components/screens/advisory/AdvisoryReviewStatusTab';
import type { AdvisoryReviewStatusTabHandle } from '@/components/screens/advisory/AdvisoryReviewStatusTab';
import type { AdvisoryType } from '@/components/screens/advisory/AdvisoryFormModal';

import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { currentUserHasPermission } from '@/utils/legacy-utils';
import { getOfficerLabel } from '@/utils/auth';
import { AccessAuthorities } from '@/models';

// ============================================================================
// AdvisoryManagementScreen — ADMIN-only oversight + configuration surface.
// ----------------------------------------------------------------------------
// Three tabs, ordered by frequency of use for supervisors/admins (2026-07-12):
// Review Status (the recurring oversight check) → Audit Log (investigative)
// → Advisory Types (rename-only, set-once config). The advisory CRUD list
// (browse/add/edit/delete) lives on the operational AdvisoryScreen shown on
// CAD/MDT. Tabs are independently permission-gated:
//   - Review Status  → ROLE_ORNA_ADVISORY_AUDIT_LOG (shared; no extra grant)
//   - Audit Log      → ROLE_ORNA_ADVISORY_AUDIT_LOG
//   - Advisory Types → ROLE_ORNA_ADVISORY_TYPE_MANAGE
// ============================================================================

type AdminTab = 'types' | 'auditLog' | 'reviewStatus';

export function AdvisoryManagementScreen() {
  const { theme } = useTheme();

  // --- Permissions / tab gating ---
  const canManageTypes = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_ORNA_ADVISORY_TYPE_MANAGE),
    []
  );
  const canViewAuditLog = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_ORNA_ADVISORY_AUDIT_LOG),
    []
  );
  // --- Tab state (default to the first tab the user can access) ---
  const initialTab: AdminTab = canViewAuditLog ? 'reviewStatus' : 'types';
  const [activeTab, setActiveTab] = useState<AdminTab>(initialTab);

  // --- Stable officer label ---
  const officerLabel = useMemo(() => getOfficerLabel(), []);

  // --- Types ---
  const [advisoryTypes, setAdvisoryTypes] = useState<AdvisoryType[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesSaving, setTypesSaving] = useState(false);

  // --- Refs ---
  const auditLogRef = useRef<AdvisoryAuditLogTabHandle>(null);
  const reviewStatusRef = useRef<AdvisoryReviewStatusTabHandle>(null);

  // ===== Load functions =====

  const loadTypes = useCallback(() => {
    setTypesLoading(true);
    api.listAdvisoryTypes(
      (result: any) => {
        const sorted = [...(result.advisoryTypes || [])].sort((a, b) => a.id - b.id);
        setAdvisoryTypes(sorted);
        setTypesLoading(false);
      },
      () => {
        App.showPopupNotification('Error getting advisory types.', 'error');
        setTypesLoading(false);
      }
    );
  }, []);

  // ===== Initial load =====

  useEffect(() => {
    if (canManageTypes) {
      loadTypes();
    }
  }, [canManageTypes, loadTypes]);

  // ===== Types tab handlers =====

  const handleTypeRename = useCallback((index: number, newType: string) => {
    setAdvisoryTypes((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], type: newType };
      return updated;
    });
  }, []);

  const handleUpdateTypes = useCallback(() => {
    if (!advisoryTypes) return;
    const advisoryTypeList = advisoryTypes.map((t) => ({ advisoryTypeId: t.id, type: t.type }));
    setTypesSaving(true);
    api.updateAdvisoryTypes(
      { advisoryTypes: advisoryTypeList },
      () => {
        App.showPopupNotification('Updated advisory types.', 'success');
        setTypesSaving(false);
        loadTypes();
      },
      () => {
        App.showPopupNotification('Failed to update advisory types.', 'error');
        setTypesSaving(false);
      }
    );
  }, [advisoryTypes, loadTypes]);

  // ===== Tabs =====

  const tabs = useMemo(() => {
    const t: Array<{ id: AdminTab; label: string }> = [];
    // Review Status reuses the audit-log permission (already held by
    // Supervisor/Admin) — no new grant to roll out.
    if (canViewAuditLog) t.push({ id: 'reviewStatus', label: 'Review Status' });
    if (canViewAuditLog) t.push({ id: 'auditLog', label: 'Audit Log' });
    if (canManageTypes) t.push({ id: 'types', label: 'Advisory Types' });
    return t;
  }, [canManageTypes, canViewAuditLog]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      const tab = tabId as AdminTab;
      setActiveTab(tab);
      if (tab === 'types') {
        loadTypes();
      } else if (tab === 'auditLog') {
        auditLogRef.current?.refresh();
      } else if (tab === 'reviewStatus') {
        reviewStatusRef.current?.refresh();
      }
    },
    [loadTypes]
  );

  // ===== Render =====

  return (
    <div className={theme}>
      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              {canViewAuditLog && (
                <li
                  className={`kst-sidebar-item${activeTab === 'reviewStatus' ? ' kst-sidebar-item--active' : ''}`}
                  onClick={() => handleTabChange('reviewStatus')}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="kst-sidebar-text">Review Status</span>
                </li>
              )}
              {canViewAuditLog && (
                <li
                  className={`kst-sidebar-item${activeTab === 'auditLog' ? ' kst-sidebar-item--active' : ''}`}
                  onClick={() => handleTabChange('auditLog')}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="kst-sidebar-text">Audit Log</span>
                </li>
              )}
              {canManageTypes && (
                <li
                  className={`kst-sidebar-item${activeTab === 'types' ? ' kst-sidebar-item--active' : ''}`}
                  onClick={() => handleTabChange('types')}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line x1="4" y1="21" x2="4" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="4" y1="10" x2="4" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="12" y1="21" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="12" y1="8" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="20" y1="21" x2="20" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="20" y1="12" x2="20" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="1" y1="14" x2="7" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="9" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="17" y1="16" x2="23" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span className="kst-sidebar-text">Advisory Types</span>
                </li>
              )}
            </ul>
          </div>

          {/* Main Content */}
          <div className="kst-main-content">
            {/* Advisory Types Tab */}
            {canManageTypes && (
              <div className="kst-page" style={{ display: activeTab === 'types' ? undefined : 'none' }}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Advisory Types</h3>
                  <p className="kst-page-description">
                    Rename the built-in advisory types. Colors are set for you and adapt to light and dark themes.
                  </p>
                </div>

                <div className="kst-alert-types-container">
                  {typesLoading && advisoryTypes.length === 0 ? (
                    <div className="loading-state"><div className="loading-spinner"></div></div>
                  ) : (
                    <>
                      {advisoryTypes.map((advisoryType, index) => (
                        <div key={advisoryType.id} className="kst-alert-type-item">
                          <div
                            className="kst-alert-type-preview"
                            style={{
                              color: '#' + advisoryType.textColor,
                              backgroundColor: '#' + advisoryType.backgroundColor,
                              borderColor: '#' + advisoryType.borderColor,
                            }}
                          >
                            {advisoryType.type}
                          </div>
                          <input
                            type="text"
                            className="kstc-input"
                            value={advisoryType.type}
                            disabled={!canManageTypes}
                            onChange={(e) => handleTypeRename(index, e.target.value)}
                          />
                        </div>
                      ))}
                      {canManageTypes && advisoryTypes.length > 0 && (
                        <div className="kst-alert-types-actions">
                          <button type="button" className="kstc-btn kstc-btn--primary" onClick={handleUpdateTypes} disabled={typesSaving}>
                            {typesSaving ? 'Saving…' : 'Save Types'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Audit Log Tab */}
            {canViewAuditLog && (
              <div className="kst-page" style={{ display: activeTab === 'auditLog' ? undefined : 'none' }}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Advisory Audit Log</h3>
                  <p className="kst-page-description">
                    A record of every advisory created, edited, deleted, restored, and viewed.
                  </p>
                </div>
                <AdvisoryAuditLogTab ref={auditLogRef} isVisible={activeTab === 'auditLog'} />
              </div>
            )}

            {/* Review Status Tab */}
            {canViewAuditLog && (
              <div className="kst-page" style={{ display: activeTab === 'reviewStatus' ? undefined : 'none' }}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Advisory Review Status</h3>
                  <p className="kst-page-description">
                    Whether each {officerLabel.toLowerCase()} has reviewed the board since it last changed, and which urgent advisories they have not yet acknowledged.
                  </p>
                </div>
                <AdvisoryReviewStatusTab ref={reviewStatusRef} isVisible={activeTab === 'reviewStatus'} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdvisoryManagementScreen;
